import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { supabase } from '../../config/supabase';
import './ArtistInfoModal.css';

const ArtistInfoModal = ({ artistName, artistImage, spotifyId, artistInfoFromAI, trackInfoFromAI = null, isTrack = false, onClose }) => {
  const { language } = useLanguage();
  const [artistInfo, setArtistInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Se è una traccia e abbiamo trackInfo dall'AI, usalo direttamente
    if (isTrack && trackInfoFromAI && trackInfoFromAI.it && trackInfoFromAI.en) {
      console.log('[ArtistInfoModal] Using track info from AI');
      setArtistInfo({
        artist_info_it: trackInfoFromAI.it,
        artist_info_en: trackInfoFromAI.en
      });
      setLoading(false);
      return;
    }
    
    // Se abbiamo artistInfo dall'AI (appena generato), usalo direttamente
    if (artistInfoFromAI && artistInfoFromAI.it && artistInfoFromAI.en) {
      console.log('[ArtistInfoModal] Using artist info from AI');
      setArtistInfo({
        artist_info_it: artistInfoFromAI.it,
        artist_info_en: artistInfoFromAI.en
      });
      setLoading(false);
    } else if (!isTrack) {
      // Altrimenti carica dal database (solo per artisti, non per tracce)
      loadArtistInfo();
    } else {
      // Per tracce senza info, mostra subito "non disponibile"
      setLoading(false);
    }
  }, [artistName, artistInfoFromAI, trackInfoFromAI, isTrack]);

  const loadArtistInfo = async () => {
    setLoading(true);
    
    try {
      console.log('[ArtistInfoModal] Loading artist info from database');
      // Carica info artista dal database
      const { data, error } = await supabase
        .from('artists')
        .select('artist_info_it, artist_info_en')
        .eq('normalized_name', artistName.toLowerCase())
        .single();

      if (error) {
        console.error('[ArtistInfoModal] Error loading artist info:', error);
        setArtistInfo(null);
      } else {
        setArtistInfo(data);
      }
    } catch (error) {
      console.error('[ArtistInfoModal] Error:', error);
      setArtistInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const getDescription = () => {
    if (!artistInfo) return null;
    return language === 'it' ? artistInfo.artist_info_it : artistInfo.artist_info_en;
  };

  // Divide il testo in paragrafi intelligenti
  const formatTextIntoParagraphs = (text) => {
    if (!text) return [];
    
    // Dividi per frasi (cerca punti seguiti da spazio e maiuscola)
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
    
    // Raggruppa in paragrafi di 2-3 frasi
    const paragraphs = [];
    const sentencesPerParagraph = 3;
    
    for (let i = 0; i < sentences.length; i += sentencesPerParagraph) {
      const paragraphSentences = sentences.slice(i, i + sentencesPerParagraph);
      paragraphs.push(paragraphSentences.join(' ').trim());
    }
    
    return paragraphs;
  };

  const description = getDescription();
  const paragraphs = description ? formatTextIntoParagraphs(description) : [];

  return (
    <div className="artist-info-modal-overlay" onClick={onClose}>
      <div className="artist-info-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="artist-info-modal-close" onClick={onClose} aria-label="Close">
          <i className="bi bi-x-lg"></i>
        </button>

        {/* Artist image */}
        <div className="artist-info-modal-image-container">
          {artistImage ? (
            <img 
              src={artistImage} 
              alt={artistName}
              className="artist-info-modal-image"
            />
          ) : (
            <div className="artist-info-modal-image-placeholder">
              <i className="bi bi-music-note-beamed"></i>
            </div>
          )}
        </div>

        {/* Artist name */}
        <h2 className="artist-info-modal-name">{artistName}</h2>

        {/* Description */}
        <div className="artist-info-modal-description">
          {loading ? (
            <div className="artist-info-modal-loading">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span>{language === 'it' ? 'Caricamento...' : 'Loading...'}</span>
            </div>
          ) : paragraphs.length > 0 ? (
            paragraphs.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))
          ) : (
            <p className="artist-info-modal-no-info">
              {language === 'it' 
                ? 'Informazioni non disponibili per questo artista.' 
                : 'Information not available for this artist.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArtistInfoModal;

